import { Modal } from 'react-bootstrap';
import {
  StockChangeReason,
  StockKind,
  StockLine,
  Warehouse,
} from '../models/StockLine';
import { useIntl } from 'react-intl';
import getWarehouseOptions from '../services/getWarehouseOptions';
import getKindOptions from '../services/getKindOptions';
import getReasonOptions from '../services/getReasonOptions';
import { useEffect, useState } from 'react';
import { SaleableProductModel } from '../../b2b-orders';
import Select from 'react-select';

interface StockModalProps {
  stockLine?: StockLine;
  onClose: () => void;
  onSave: (stockLine: StockLine) => void;
  visible?: boolean;
  products?: Array<SaleableProductModel>;
}

export default function StockModal({
  stockLine = null,
  onClose,
  onSave,
  visible = false,
  products = [],
}: StockModalProps) {
  const intl = useIntl();
  const warehouseOptions = getWarehouseOptions(intl, false);
  const kindOptions = getKindOptions(intl, false);
  const reasonOptions = getReasonOptions(intl, false);
  const productOptions = products.map((product) => ({
    value: product.id,
    label: `${product.name} - ${product.ean}`,
  }));
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(
    null
  );
  const [selectedKind, setSelectedKind] = useState<StockKind | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [reason, setReason] = useState<StockChangeReason | null>(null);

  function handleSave() {
    onSave({
      ...stockLine,
      warehouse: selectedWarehouse,
      kind: selectedKind,
      product: selectedProduct,
      quantity: quantity,
      reason: reason,
    });
  }

  useEffect(() => {
    if (stockLine && visible) {
      setSelectedWarehouse(stockLine.warehouse);
      setSelectedKind(stockLine.kind);
      setSelectedProduct(stockLine.product);
      setQuantity(stockLine.quantity);
      setReason(stockLine.reason);
    }
    if (!stockLine && visible) {
      setSelectedWarehouse(null);
      setSelectedKind(null);
      setSelectedProduct(null);
      setQuantity(null);
      setReason(null);
    }
  }, [stockLine, visible]);

  useEffect(() => {
    console.log({
      selectedWarehouse,
      selectedKind,
      selectedProduct,
      quantity,
      reason,
    });
  }, [selectedWarehouse, selectedKind, selectedProduct, quantity, reason]);

  return (
    <Modal
      show={visible}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {stockLine
            ? intl.formatMessage({ id: 'stock.modifyStock' })
            : intl.formatMessage({ id: 'stock.registerStock' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className="d-flex flex-column gap-5">
          <div className="form-group">
            <label htmlFor="warehouse" className="form-label">
              {intl.formatMessage({ id: 'stock.warehouse' })}
            </label>
            <select
              id="warehouse"
              className="form-control form-select"
              value={selectedWarehouse ? selectedWarehouse : ''}
              onChange={(e) =>
                e.target.value
                  ? setSelectedWarehouse(
                      parseInt(e.target.value, 10) as unknown as Warehouse
                    )
                  : setSelectedWarehouse(null)
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'stock.selectWarehouse' })}
              </option>
              {warehouseOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="kind" className="form-label">
              {intl.formatMessage({ id: 'stock.kind' })}
            </label>
            <select
              id="kind"
              className="form-control form-select"
              value={selectedKind ? selectedKind : ''}
              onChange={(e) =>
                e.target.value
                  ? setSelectedKind(e.target.value as unknown as StockKind)
                  : setSelectedKind(null)
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'stock.selectKind' })}
              </option>
              {kindOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="product" className="form-label">
              {intl.formatMessage({ id: 'stock.product' })}
            </label>
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={productOptions}
              value={
                productOptions.find(
                  (option) => option.value === selectedProduct
                ) || null
              }
              onChange={(option) => setSelectedProduct(option.value)}
              placeholder={intl.formatMessage({
                id: 'orderDetail.searchProductPlaceholder',
              })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="quantity" className="form-label">
              {intl.formatMessage({ id: 'stock.quantity' })}
            </label>
            <input
              type="number"
              id="quantity"
              className="form-control"
              value={quantity}
              onChange={(e) =>
                e.target.value?.length > 0
                  ? setQuantity(parseInt(e.target.value, 10))
                  : setQuantity(null)
              }
              min={0}
              step={1}
            />
          </div>
          <div className="form-group">
            <label htmlFor="reason" className="form-label">
              {intl.formatMessage({ id: 'stock.reason' })}
            </label>
            <select
              id="reason"
              className="form-control form-select"
              value={reason}
              onChange={(e) =>
                e.target.value
                  ? setReason(e.target.value as StockChangeReason)
                  : setReason(null)
              }
            >
              <option value="" disabled>
                {intl.formatMessage({ id: 'stock.selectReason' })}
              </option>
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </section>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-text" onClick={onClose}>
          {intl.formatMessage({ id: 'stock.cancel' })}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            !selectedWarehouse ||
            !selectedKind ||
            !selectedProduct ||
            typeof quantity !== 'number' ||
            quantity <= -1
          }
          onClick={handleSave}
        >
          {stockLine
            ? intl.formatMessage({ id: 'stock.saveChanges' })
            : intl.formatMessage({ id: 'stock.createStockLine' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
